import {
  WSActions,
  WSElement,
  WSSectionToolbar,
  WSTable,
  WSTableCell
} from "@wingspanhq/fe-component-library";
import { IWorkLog } from "@wingspanhq/payments/dist/interfaces";
import queryString from "qs";
import React, { useMemo } from "react";
import { useHistory } from "react-router-dom";
import { useUserId } from "../../../query/hooks/helpers";
import { useQueryWorkLogsWithPayees } from "../../../query/workLog/queries/useQueryWorkLogsWithPayees";
import { IPayeeRow } from "../../../services/search";
import { usePayablesMap } from "../hooks/usePayablesMap";
import { getPathWorkLogPayerDetails, PATH_WORK_LOGS_PAYER } from "../paths";
import { getWorkLogPayerStatusCellProps } from "../utils/getWorkLogPayerStatusCellProps";
import { createListRequest } from "./TableWorkLogsPayerView/filters/request";
import { Filter } from "./TableWorkLogsPayerView/filters/types";

type Props = {
  filter?: Filter;
  title?: string;
  showEngagementDetails?: boolean;
  hideViewAll?: boolean;
};

export const WidgetWorkLogsPayerView: React.FC<Props> = ({
  filter,
  title = "Work logs",
  showEngagementDetails = false,
  hideViewAll = false
}) => {
  const userId = useUserId();
  const history = useHistory();

  // Build API request with 5-item page size
  const request = useMemo(
    () => ({
      ...createListRequest(userId, filter),
      page: { size: 5, number: 1 }
    }),
    [userId, filter]
  );

  const queryWorkLogs = useQueryWorkLogsWithPayees(request, {
    joinPayee: showEngagementDetails
  });
  const workLogs = useMemo(
    () => queryWorkLogs.data || [],
    [queryWorkLogs.data]
  );
  const { payablesMap } = usePayablesMap(workLogs);

  // Build payeesMap from work logs with joined payee data
  const payeesMap = useMemo(() => {
    const map = new Map();
    if (showEngagementDetails) {
      (workLogs || []).forEach((workLog: any) => {
        if (workLog.payee) {
          map.set(workLog.payeeId, workLog.payee);
        }
      });
    }
    return map;
  }, [showEngagementDetails, workLogs]);

  // Build engagement names map: workLogId -> engagementName
  const engagementNamesMap = useMemo(() => {
    if (!showEngagementDetails) return new Map<string, string>();

    const map = new Map<string, string>();
    workLogs.forEach(workLog => {
      const payee = payeesMap.get(workLog.payeeId);
      const engagement = payee?.engagements?.find(
        (e: IPayeeRow["engagements"][number]) =>
          e.payerPayeeEngagementId === workLog.payerPayeeEngagementId
      );
      if (engagement?.engagementName) {
        map.set(workLog.workLogId, engagement.engagementName);
      }
    });
    return map;
  }, [showEngagementDetails, workLogs, payeesMap]);

  // Build external IDs map: workLogId -> externalId
  const externalIdsMap = useMemo(() => {
    if (!showEngagementDetails) return new Map<string, string>();

    const map = new Map<string, string>();
    workLogs.forEach(workLog => {
      const payee = payeesMap.get(workLog.payeeId);
      const externalId = payee?.payerOwnedData?.payeeExternalId;
      if (externalId) {
        map.set(workLog.workLogId, externalId);
      }
    });
    return map;
  }, [showEngagementDetails, workLogs, payeesMap]);

  // Helper to build secondary text from the two maps
  const getSecondaryText = (workLogId: string): string | undefined => {
    const engagementName = engagementNamesMap.get(workLogId);
    const externalId = externalIdsMap.get(workLogId);

    if (engagementName && externalId) {
      return `${engagementName} • ${externalId}`;
    }
    return engagementName || externalId;
  };

  if (!queryWorkLogs.data?.length) {
    return null;
  }

  return (
    <WSElement>
      <WSSectionToolbar title={title} mb="S" />

      <WSTable<IWorkLog>
        tableData={workLogs.map(workLog => ({
          id: workLog.workLogId,
          data: workLog
        }))}
        onRowClick={row => {
          history.push(getPathWorkLogPayerDetails(row.id));
        }}
        columns={[
          {
            config: {
              header: { text: "Work logs" },
              gridTemplateSizeMax: "2fr"
            },
            renderFunction: ({ data }) => (
              <WSTableCell
                text={`#${data.workLogNumber}`}
                secondaryText={
                  showEngagementDetails
                    ? getSecondaryText(data.workLogId)
                    : undefined
                }
              />
            )
          },
          {
            config: {
              header: { text: "Status" },
              justify: "end"
            },
            renderFunction: ({ data }) => {
              const payable = payablesMap?.get(data.invoiceId || "");
              const cellProps = getWorkLogPayerStatusCellProps({
                workLog: data,
                payable
              });
              return <WSTableCell {...cellProps} />;
            }
          }
        ]}
      />

      {!hideViewAll && (
        <WSActions
          alignment="left"
          size="M"
          mt="M"
          buttons={[
            {
              label: "View all",
              kind: "Link",
              onClick: () => {
                const query = queryString.stringify({
                  filter: filter || {}
                });
                history.push(`${PATH_WORK_LOGS_PAYER}?${query}`);
              }
            }
          ]}
        />
      )}
    </WSElement>
  );
};
